import { CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Div from '@jumbo/shared/Div';
import storeServices from 'app/services/automations/store-services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import deviceServices from 'app/services/automations/device-services';
import DeviceCard from 'app/shared/devices/DeviceCard';
import DeviceDetailPanel from 'app/shared/devices/DeviceDetailPanel';
import DeviceEditModal from '../devices/DeviceEditModal';
import { StandardFiltersBar, StandardPageHeader, StandardSearchField } from 'app/shared/ui';

// Igual que DevicesController::MAX_PER_PAGE en el backend.
const MAX_DEVICES = 500;

const StoreShow = () => {
    const UNASSIGNED_ZONE = '__UNASSIGNED_ZONE__'
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [store, setStore] = useState(null)
    const [recordsList, setRecordsList] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [searchQuery, setSearchQuery] = useState('')
    const [listLoading, setListLoading] = useState(false)
    const [openRecordEditModal, setOpenRecordEditModal] = useState(false)
    const [currentDeviceID, setCurrentDeviceID] = useState(null)
    const [draggingDeviceID, setDraggingDeviceID] = useState(null)
    const [dragOverZone, setDragOverZone] = useState(null)
    const dragPreviewNodeRef = useRef(null)
    const existingZoneOptions = _.uniq(_.compact(_.map(recordsList, 'zone'))).sort((a, b) => a.localeCompare(b))
    const openedDeviceId = searchParams.get('device')

    const openDeviceDetail = (deviceId) => {
        if(!deviceId) return
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('device', `${deviceId}`)
        setSearchParams(nextParams)
    }

    const closeDeviceDetail = () => {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('device')
        setSearchParams(nextParams)
    }

    useEffect(() => {
        if(listLoading){
            loadStore()
            loadRecords()
        }
    }, [listLoading])

    useEffect(() => {
        setListLoading(true)
    }, [])

    const buildRecordParams = () => {
        let par = []

        if(searchQuery) {
            par.push(encodeURI(`q[name_or_kind_or_zone_cont]=${searchQuery}`))
        }
        // Sin paginación: el local muestra todos sus dispositivos, agrupados por zona.
        par.push(`per_page=${MAX_DEVICES}`)
        par.push(`include=device_type.measurement_types,latest_device_metric`)
        par.push(encodeURI(`q[store_id_eq]=${id}`))
        par.push(encodeURI(`q[s][]=zone asc`))
        par.push(encodeURI(`q[s][]=name asc`))

        par = _.join(par, '&')

        return `?${par}`;
    }

    const loadStore = () => {
        storeServices.getStore(id)
            .then(resp => {
                setStore(resp)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }

    const loadRecords = () => {
        deviceServices.getDevices(buildRecordParams())
            .then(resp => {
                setRecordsList(resp.data)
                setTotalCount(resp?.meta?.total_count || resp?.data?.length || 0)
            })
            .catch(function (error) {})
            .finally(() => {
                setListLoading(false)
            });
    }

    const normalizeDeviceResponse = (payload) => {
        if(payload?.data?.id && payload?.data?.attributes){
            return {
                id: payload.data.id,
                ...payload.data.attributes,
            }
        }
        return payload
    }

    const handleRecordChange = (newRec) => {
        const normalizedRecord = normalizeDeviceResponse(newRec)
        if(!normalizedRecord?.id) return

        const resolvedStoreId = normalizedRecord?.store_id || normalizedRecord?.store?.id || store?.id || id
        if(`${resolvedStoreId}` !== `${id}`) {
            setRecordsList((prev) => _.filter(prev, (r) => `${r.id}` !== `${normalizedRecord.id}`))
            return
        }

        setRecordsList((prev) => _.orderBy(
            _.map(prev, (r) => (`${r.id}` === `${normalizedRecord.id}` ? normalizedRecord : r)),
            ['zone', 'name'],
            ['asc', 'asc']
        ))
    }

    const handleRecordAdd = (newRec) => {
        const normalizedNewRec = normalizeDeviceResponse(newRec)
        if(!normalizedNewRec?.id) return

        const resolvedStoreId = normalizedNewRec?.store_id || normalizedNewRec?.store?.id || store?.id || id
        if(`${resolvedStoreId}` !== `${id}`) return

        setRecordsList((prev) => _.orderBy(_.uniqBy([normalizedNewRec, ...prev], 'id'), ['zone', 'name'], ['asc', 'asc']))
    }

    const normalizeZoneKey = (zone) => zone || UNASSIGNED_ZONE
    const denormalizeZoneKey = (zoneKey) => zoneKey === UNASSIGNED_ZONE ? null : zoneKey

    const getZoneLabel = (zoneKey) => {
        if(zoneKey === UNASSIGNED_ZONE) return 'Sin zona'
        return zoneKey
    }

    const handleDropOnZone = (targetZoneKey, droppedDeviceID = draggingDeviceID) => {
        if(!droppedDeviceID) {
            setDragOverZone(null)
            return
        }

        const targetZone = denormalizeZoneKey(targetZoneKey)
        const device = _.find(recordsList, (r) => `${r.id}` === `${droppedDeviceID}`)
        if(!device){
            setDragOverZone(null)
            setDraggingDeviceID(null)
            return
        }

        const sourceZoneKey = normalizeZoneKey(device.zone)
        if(sourceZoneKey === targetZoneKey){
            setDragOverZone(null)
            setDraggingDeviceID(null)
            return
        }

        const previousList = recordsList
        const optimistDevice = { ...device, zone: targetZone }
        setRecordsList(_.map(recordsList, (r) => `${r.id}` === `${droppedDeviceID}` ? optimistDevice : r))
        setDragOverZone(null)
        setDraggingDeviceID(null)

        deviceServices.updateDevice(device.id, '?include=device_type.measurement_types,latest_device_metric', {
            device: {
                name: device?.name || '',
                kind: device?.kind || '',
                zone: targetZone || '',
                store_id: device?.store_id || store?.id || id,
            }
        }).then((resp) => {
            handleRecordChange(resp)
        }).catch(() => {
            setRecordsList(previousList)
        })
    }

    const clearDragPreview = () => {
        if(dragPreviewNodeRef.current && dragPreviewNodeRef.current.parentNode){
            dragPreviewNodeRef.current.parentNode.removeChild(dragPreviewNodeRef.current)
        }
        dragPreviewNodeRef.current = null
    }

    const handleDeviceDragStart = (e, deviceId) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', `${deviceId}`)
        setDraggingDeviceID(deviceId)

        // La manilla dispara el arrastre, pero lo que se ve arrastrando es la tarjeta entera.
        const sourceElement = e.currentTarget?.closest('[data-device-card]') || e.currentTarget
        if(!sourceElement || !e.dataTransfer?.setDragImage) return

        clearDragPreview()
        const rect = sourceElement.getBoundingClientRect()
        const previewNode = sourceElement.cloneNode(true)
        previewNode.style.position = 'fixed'
        previewNode.style.top = '-2000px'
        previewNode.style.left = '-2000px'
        previewNode.style.width = `${rect.width}px`
        previewNode.style.height = `${rect.height}px`
        previewNode.style.margin = '0'
        previewNode.style.pointerEvents = 'none'
        previewNode.style.opacity = '0.95'
        previewNode.style.boxSizing = 'border-box'
        previewNode.style.transform = 'none'
        document.body.appendChild(previewNode)
        dragPreviewNodeRef.current = previewNode

        e.dataTransfer.setDragImage(previewNode, rect.width / 2, rect.height / 2)
    }

    const handleDeviceDragEnd = () => {
        clearDragPreview()
        setDragOverZone(null)
        setDraggingDeviceID(null)
    }

    const handleSearchQueryChange = (newVal) => {
        setSearchQuery(newVal)
        setListLoading(true)
    }

    const debounceSearchQueryChange = useMemo(() => _.debounce(handleSearchQueryChange, 450), [])

    useEffect(() => {
        return () => {
            debounceSearchQueryChange.cancel()
        }
    }, [debounceSearchQueryChange])

    const renderRecordsList = () => {
        if(recordsList && recordsList.length > 0){
            const zones = _.groupBy(recordsList, (record) => normalizeZoneKey(record.zone));
            const sortedZoneKeys = _.orderBy(
                _.keys(zones),
                [(zoneKey) => zoneKey === UNASSIGNED_ZONE ? 1 : 0, (zoneKey) => zoneKey.toLowerCase()],
                ['asc', 'asc']
            )
            return (
                <Grid container spacing={3}>
                    {_.map(sortedZoneKeys, (zoneKey) => {
                        const zoneDevices = zones[zoneKey] || []

                        return (
                        <Grid item xs={12} key={`zone-${zoneKey}`}>
                            <Paper
                                onDragOver={(e) => { e.preventDefault(); setDragOverZone(zoneKey) }}
                                onDragLeave={() => { if(dragOverZone === zoneKey) setDragOverZone(null) }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const droppedId = e.dataTransfer?.getData('text/plain')
                                    handleDropOnZone(zoneKey, droppedId || draggingDeviceID)
                                }}
                                sx={{
                                    backgroundColor: dragOverZone === zoneKey ? 'rgba(25, 118, 210, 0.06)' : 'background.section',
                                    borderWidth: 1,
                                    borderStyle: 'dashed',
                                    borderColor: dragOverZone === zoneKey ? 'primary.main' : '#DEE2E6',
                                    borderRadius: 2,
                                    padding: '16px',
                                    marginBottom: '16px',
                                    transition: 'all 0.2s ease',
                                }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                                    <Typography variant="h6">{getZoneLabel(zoneKey)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {zoneDevices.length} sensor{zoneDevices.length === 1 ? '' : 'es'}
                                    </Typography>
                                </Stack>
                                <Grid container spacing={2.5}>
                                    {_.map(zoneDevices, (r) => (
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                            lg={4}
                                            xl={3}
                                            key={`device-${r.id}`}
                                            sx={{ cursor: 'default' }}>
                                            <DeviceCard
                                                device={r}
                                                onOpen={(device) => openDeviceDetail(device?.id)}
                                                dragHandleProps={{
                                                    draggable: true,
                                                    onDragStart: (e) => handleDeviceDragStart(e, r.id),
                                                    onDragEnd: handleDeviceDragEnd,
                                                }}
                                                onEditClick={(deviceId) => { setCurrentDeviceID(deviceId); setOpenRecordEditModal(true); }}
                                                onAlertClick={(device) => navigate(`/automations/rules?device_id=${device?.id || r?.id}`)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>
                    )})}
                </Grid>
            )
        } else {
            return(
                <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h6" mb={1}>No hay sensores en este local</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ajusta tu búsqueda o revisa la configuración de filtros.
                    </Typography>
                </Paper>
            )
        }
    }

    return (
        <div>
            <DeviceDetailPanel
                deviceId={openedDeviceId}
                onClose={closeDeviceDetail}
                onEditDevice={(deviceId) => { setCurrentDeviceID(deviceId); setOpenRecordEditModal(true); }}
                onOpenAlerts={(device) => navigate(`/automations/rules?device_id=${device?.id}`)}
            />
            <DeviceEditModal
                recordID={currentDeviceID}
                isOpen={openRecordEditModal}
                defaultStoreId={store?.id || id}
                defaultStore={store}
                zoneOptions={existingZoneOptions}
                onClose={() => { setOpenRecordEditModal(false) }}
                onRecordChange={handleRecordChange}
                onRecordAdd={handleRecordAdd}
            />
            <StandardPageHeader
                title={store?.name || 'Local'}
                subtitle={`${totalCount || 0} dispositivos en este local.`}
                backTo="/automations/stores"
                backLabel="Volver a mis locales"
            />

            <StandardFiltersBar>
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <StandardSearchField
                            fullWidth
                            placeholder="Buscar por nombre, tipo o zona"
                            onChange={(e) => debounceSearchQueryChange(e.target.value)}
                            onEnter={(value) => handleSearchQueryChange(value)}
                        />
                    </Grid>
                </Grid>
            </StandardFiltersBar>

            <Div sx={{display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                <CircularProgress/>
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Arrastra y suelta un sensor sobre otra zona para moverlo.
                    </Typography>
                    {renderRecordsList()}
                </Grid>
            </Grid>
        </div>
    );
};

export default StoreShow;
